import { useState } from "react";
import { Checkbox, SearchField, Text, TextStyle } from "@via-ds/components";
import Cookies from "js-cookie";
import { useQueryParam } from "@evg-ui/lib/hooks";
import { usePageTitle } from "@evg-ui/lib/hooks/usePageTitle";
import { useProjectPatchesAnalytics, useUserPatchesAnalytics } from "analytics";
import { FiltersWrapper, PageWrapper } from "components/styles";
import { INCLUDE_HIDDEN_PATCHES } from "constants/cookies";
import { PatchesPagePatchesFragment } from "gql/generated/types";
import { PatchPageQueryParams } from "types/patch";
import { validateRegexp } from "utils/validators";
import styles from "./index.module.css";
import ListArea from "./ListArea";
import { PaginationButtons } from "./PaginationButtons";
import { StatusSelector } from "./StatusSelector";

interface Props {
  filterComp?: React.ReactNode;
  loading: boolean;
  pageTitle: string;
  pageType: "project" | "user";
  patches?: PatchesPagePatchesFragment;
}

export const PatchesPage: React.FC<Props> = ({
  filterComp,
  loading,
  pageTitle,
  pageType,
  patches,
}) => {
  usePageTitle(pageTitle);
  const userPatchesAnalytics = useUserPatchesAnalytics();
  const projectPatchesAnalytics = useProjectPatchesAnalytics();
  const analytics =
    pageType === "project" ? projectPatchesAnalytics : userPatchesAnalytics;

  const [patchName, setPatchName] = useQueryParam<string>(
    PatchPageQueryParams.PatchName,
    "",
  );
  const [invalidPatchNameInput, setInvalidPatchNameInput] = useState<
    string | null
  >(null);
  const patchNameInput = invalidPatchNameInput ?? patchName;

  // Handle filtering by patch description.
  const handleInputChange = (value: string) => {
    if (!validateRegexp(value)) {
      setInvalidPatchNameInput(value);
      return;
    }

    setInvalidPatchNameInput(null);
    setPatchName(value);
    analytics.sendEvent({
      name: "Filtered for patches",
      "filter.by": value,
    });
  };

  // Handle filtering for hidden patches.
  const [includeHiddenCheckboxChecked, setIsIncludeHiddenCheckboxChecked] =
    useQueryParam(
      PatchPageQueryParams.Hidden,
      Cookies.get(INCLUDE_HIDDEN_PATCHES) === "true",
    );
  const includeHiddenCheckboxOnChange = (isSelected: boolean): void => {
    setIsIncludeHiddenCheckboxChecked(isSelected);
    Cookies.set(INCLUDE_HIDDEN_PATCHES, isSelected ? "true" : "false");
    analytics.sendEvent({
      name: "Filtered for patches",
      "filter.hidden": isSelected,
    });
  };

  const filteredCount = patches?.filteredPatchCount ?? 0;

  return (
    <PageWrapper>
      <Text
        className={styles.pageTitle}
        data-testid="patches-page-title"
        elementType="h2"
        textStyle={TextStyle.heading2}
      >
        {pageTitle}
      </Text>
      <FiltersWrapper className={styles.filtersWrapperSpaceBetween}>
        <SearchField
          aria-label="Search patch descriptions"
          data-testid="patch-description-input"
          errorMessage="Invalid regex"
          isInvalid={!validateRegexp(patchNameInput)}
          onChange={handleInputChange}
          placeholder="Patch description regex"
          value={patchNameInput}
        />
        <StatusSelector />
        {filterComp}
        <Checkbox
          className={styles.hiddenCheckbox}
          data-testid="include-hidden-checkbox"
          isSelected={includeHiddenCheckboxChecked}
          onChange={includeHiddenCheckboxOnChange}
        >
          Include hidden
        </Checkbox>
      </FiltersWrapper>
      <PaginationButtons
        filteredPatchCount={filteredCount}
        loading={loading}
        pageType={pageType}
      />
      <ListArea
        loading={loading}
        pageType={pageType}
        patches={patches?.patches || []}
      />
      {!loading && filteredCount > 10 ? (
        <PaginationButtons
          filteredPatchCount={filteredCount}
          pageType={pageType}
        />
      ) : null}
    </PageWrapper>
  );
};
